// note: general import
import * as React from "react";

// note: component import
import { MCCustomizedTooltipBV6 } from "components/NonReusable/Tooltip";

// note: MUI import
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import { useTheme } from "@mui/material/styles";

// import { styled, alpha } from "@mui/material/styles";
// import SearchIcon from "@mui/icons-material/Search";
// import InputBase from '@mui/material/InputBase';
// import IconButton from '@mui/material/IconButton';
// import MenuIcon from "@mui/icons-material/Menu";
// import SearchIcon from '@mui/icons-material/Search';
// import DirectionsIcon from "@mui/icons-material/Directions";

// note: utils import
import {
  VisibilityOutlinedIcon,
  VisibilityOffOutlinedIcon,
  Search,
} from "utils/asset";

// variant = outlined, filled, standard
export function MCTextFieldBV1({
  // MC = MUI Core
  type = "text",
  required = false,
  variant = "outlined",
  onChange,
  error = false,
  helperText,
  id,
  name,
  label,
  size = "small",
  value,
  disabled,
  sx,
  shrink,
  inputProps = {
    style: { fontSize: "14px" },
  },
  InputLabelProps = {
    style: { fontSize: "14px" },
  },
}) {
  return (
    <TextField
      type={type}
      required={required}
      error={error}
      id={id}
      name={name}
      label={label}
      variant={variant}
      helperText={helperText}
      onChange={onChange}
      size={size}
      value={value}
      disabled={disabled}
      sx={sx}
      inputProps={inputProps}
      InputLabelProps={{ ...InputLabelProps, shrink: shrink }}
    />
  );
}

export function MCTextFieldBV2({
  // MC = MUI Core
  type = "text",
  required = false,
  variant = "standard",
  onChange,
  error = false,
  helperText,
  id,
  name,
  label,
  size = "small",
  value,
  disabled,
  sx,
  shrink,
  input,
  inputLabel,
}) {
  return (
    <TextField
      type={type}
      required={required}
      error={error}
      id={id}
      name={name}
      label={label}
      variant={variant}
      helperText={helperText}
      onChange={onChange}
      size={size}
      value={value}
      disabled={disabled}
      sx={{ paddingTop: 1, paddingBottom: 1, ...sx }}
      slotProps={{
        input: { input },
        inputLabel: { ...inputLabel, shrink: shrink },
      }}
    />
  );
}

export function MCPasswordTextFieldBV2({
  // MC = MUI Core
  type,
  name,
  onClick,
  onMouseDown,
  showPassword,
  label = "Password",
  size = "small",
  inputLabel = "Password",
  onChange,
  value,
  id,
  htmlFor,
}) {
  return (
    <FormControl variant="outlined">
      <InputLabel htmlFor={htmlFor} size="small" sx={{ fontSize: "14px" }}>
        {inputLabel}
      </InputLabel>
      <OutlinedInput
        id={id}
        type={type}
        onChange={onChange}
        value={value}
        name={name}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={onClick}
              onMouseDown={onMouseDown}
              edge="end"
            >
              {showPassword ? (
                <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
              ) : (
                <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </InputAdornment>
        }
        label={label}
        size={size}
        inputProps={{
          style: {
            fontSize: "14px",
            height: "auto",
          },
        }}
        InputLabelProps={{
          style: {
            fontSize: "12px",
          },
        }}
      />
    </FormControl>
  );
}

export function MCEmailTextFieldBV3({
  type = "text",
  name,
  label = "Email",
  size = "small",
  inputLabel = "Email",
  onChange,
  value,
  id,
  htmlFor,
  disabled,
  endAdornment,
}) {
  // MC = MUI Core
  return (
    <FormControl variant="outlined">
      <InputLabel htmlFor={htmlFor} size="small" sx={{ fontSize: "14px" }}>
        {inputLabel}
      </InputLabel>
      <OutlinedInput
        id={id}
        type={type}
        onChange={onChange}
        value={value}
        name={name}
        disabled={disabled}
        endAdornment={endAdornment}
        label={label}
        size={size}
        inputProps={{
          style: {
            fontSize: "14px",
            height: "auto",
          },
        }}
        InputLabelProps={{
          style: {
            fontSize: "14px",
          },
        }}
      />
    </FormControl>
  );
}

export function MCSearchBV4({
  resetComponent = null,
  pickDateComponent = null,
  onChange,
  value,
  placeholder = "Search",
  sentenceSearchTooltip,
}) {
  // note: declare theme
  const theme = useTheme();

  return (
    <Paper
      component="form"
      sx={{ p: "2px 4px", display: "flex", alignItems: "center", width: 400 }}
    >
      {/* note: reset component */}
      {resetComponent}
      {resetComponent && (
        <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
      )}

      {/* note: inpute base */}

      <MCCustomizedTooltipBV6 sentences={sentenceSearchTooltip}>
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder={placeholder}
          inputProps={{ "aria-label": "search" }}
          onChange={onChange}
          value={value}
        />
      </MCCustomizedTooltipBV6>
      <IconButton type="button" aria-label="search" size="small">
        <Search
          style={{
            color: theme.palette.icon.main,
            width: "20px",
            height: "20px",
          }}
        />
      </IconButton>

      {/* note: pick date component */}
      {pickDateComponent && (
        <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
      )}
      {pickDateComponent}
    </Paper>
  );
}

export function MCEmailTextFieldBV5({
  type = "text",
  name,
  label = "Email",
  size = "small",
  inputLabel = "Email",
  onChange,
  value,
  id,
  htmlFor,
  disabled,
  endAdornment,
  variant = "filled",
}) {
  // MC = MUI Core

  return (
    <FormControl variant={variant}>
      <InputLabel htmlFor={htmlFor}>{inputLabel}</InputLabel>
      <Input
        sx={{ paddingTop: 1, paddingBottom: 1 }}
        id={id}
        type={type}
        onChange={onChange}
        value={value}
        name={name}
        disabled={disabled}
        endAdornment={endAdornment}
        label={label}
        size={size}
      />
    </FormControl>
  );
}

export function MCPasswordTextFieldBV6({
  // MC = MUI Core
  type,
  name,
  onClick,
  onMouseDown,
  showPassword,
  label = "Password",
  size = "small",
  inputLabel = "Password",
  onChange,
  value,
  id,
  htmlFor,
  variant = "outlined",
}) {
  return (
    <FormControl variant={variant}>
      <InputLabel htmlFor={htmlFor}>{inputLabel}</InputLabel>
      <Input
        id={id}
        type={type}
        onChange={onChange}
        value={value}
        name={name}
        sx={{ paddingTop: 1, paddingBottom: 1 }}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={onClick}
              onMouseDown={onMouseDown}
              edge="end"
            >
              {showPassword ? (
                <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
              ) : (
                <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </InputAdornment>
        }
        label={label}
        size={size}
      />
    </FormControl>
  );
}
